// The one masthead every planning page wears (Scott, 2026-09-06: "the day bar
// is a nice visual anchor" — bring the SHAPE everywhere, not the day's
// content). Today grew this card first; Week and the Month/Season/Year pages
// now share it, each filling the slots with its own period:
//
//   eyebrow   the period nav — "‹ SUNDAY · SEPTEMBER 6 ⌄ ›", "‹ WEEK ›"
//   title     the serif headline — a greeting on Today, the dates on Week,
//             "This Month" on the period pages
//   subline   one quiet line — Next: …, the range presets, a look-back cue
//   controls  page chrome in the corner (domain chooser, assistant toggle)
//   aside     the ear: one quiet line at the right of the subline (Today's
//             weather); on phones it drops under the subline
//   footer    the page's own controls along the foot (Today's dropdowns)
//
// The masthead is now an OPEN heading rather than a rounded illustrated card
// (parity pass 2026-09-17): a rule above, the serif headline, a hairline
// below. Three variants, all the same shell:
//
//   'daybook'  Today — plus the big date numeral in the left margin
//   'page'     every other surface — the period pages, Inbox, the library;
//              the surface's motif rides along as a small stamp, not a wash
//   'card'     the older illustrated card, kept for anything still on it
import { PageHelpButton } from '@/components/explain/PageHelp'
import { LearnVideoChip } from '@/components/learn/LearnVideoChip'
import type { ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { PlaceWash } from '@/components/place/PlaceWash'
import { PageMotif, type MotifId } from '@/components/place/motifs/PageMotif'

export function MastheadCard({ eyebrow, title, subline, controls, aside, footer, motif, variant = 'card', date, className = '', action, numeral, titleNav, help = true }: {
  eyebrow?: ReactNode
  title: ReactNode
  /** Prev/next arrows either side of the title — "‹ Oct 17 – 23 ›" (Week,
   *  calm pass 2) — with `trailing` hung beside them (a popover). */
  titleNav?: { onPrev: () => void; onNext: () => void; prevLabel: string; nextLabel: string; trailing?: ReactNode }
  /** The ? and ▷ beside the title. Week keeps them in its ⋯ menu instead. */
  help?: boolean
  /** The page's one primary action, beside the title at every width —
   *  Today's "Add task". `controls` is desktop chrome; this is the verb. */
  action?: ReactNode
  subline?: ReactNode
  controls?: ReactNode
  /** The masthead's ear — a short line that belongs to the heading but not
   *  to the sentence under it (Today's weather). */
  aside?: ReactNode
  footer?: ReactNode
  /** The surface's own icon. Omitted on the rhythm pages, which wear the
   *  place — they are one continuous stretch of the same life, not different
   *  kinds of thing. */
  motif?: MotifId
  /** Today uses an open daybook masthead; other surfaces keep their card. */
  variant?: 'card' | 'daybook' | 'page'
  date?: Date
  className?: string
  /** The horizon's big thin number in the margin — "40" on Week (planning
   *  v2, 2026-09-28). Absent, nothing changes. */
  numeral?: string
}) {
  const open = variant !== 'card'
  const heading = (
    <h1 className={open ? 'daybook-title' : 'font-display text-[28px] font-semibold leading-tight text-neutral-950 md:text-[34px]'}>
      {title}
      {/* "What is this page?", where the page has a guide, and its
          short video beside it until watched (learnVideos.ts). */}
      {help && <><PageHelpButton /><LearnVideoChip /></>}
    </h1>
  )
  return (
    <section
      data-testid="masthead-card"
      data-explain="page-title"
      className={`${open
        ? `daybook-masthead${variant === 'page' ? ' daybook-masthead-page' : ''}`
        : 'relative mx-3 mb-4 rounded-2xl border border-neutral-200/80 bg-bg-elevated shadow-sm md:mx-0'} ${className}`}
    >
      {variant === 'card' && <PlaceWash motif={motif} />}
      <div className={open ? 'daybook-masthead-inner' : 'relative px-4 py-4 md:px-5'}>
        <div className={`daybook-masthead-row flex items-start justify-between gap-4${aside ? ' flex-wrap md:flex-nowrap' : ''}`}>
          {variant === 'daybook' && date && (
            <div aria-hidden="true" className="daybook-date">
              <span>{date.toLocaleDateString('en-US', { month: 'short' })}</span>
              <strong>{date.getDate().toString().padStart(2, '0')}</strong>
            </div>
          )}
          {/* The motif keeps the page's identity without the wash it used to
              sit behind: one small stamp, at full strength, beside the name. */}
          {variant === 'page' && motif && (
            /* Decorative: the heading beside it already names the page, so the
               motif's own label would just be read out twice. */
            <span aria-hidden="true" className="contents"><PageMotif motif={motif} className="daybook-stamp" /></span>
          )}
          {numeral && <div aria-hidden="true" className="masthead-numeral" data-len={numeral.length}>{numeral}</div>}
          <div className="daybook-masthead-text min-w-0 flex-1">
            {eyebrow && <div data-testid="masthead-eyebrow" className="mb-1 -ml-1.5">{eyebrow}</div>}
            <div className={action ? 'daybook-title-row' : undefined}>
              {titleNav ? (
                <div data-testid="masthead-title-nav" className="daybook-title-nav">
                  <button type="button" aria-label={titleNav.prevLabel} onClick={titleNav.onPrev} className="daybook-title-arrow">
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  {heading}
                  <button type="button" aria-label={titleNav.nextLabel} onClick={titleNav.onNext} className="daybook-title-arrow">
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                  {titleNav.trailing}
                </div>
              ) : heading}
              {action && <div className="daybook-title-action">{action}</div>}
            </div>
            {subline && <div className="mt-1 max-w-2xl text-sm text-neutral-500 md:text-[15px]">{subline}</div>}
          </div>
          {controls && <div className="daybook-masthead-controls hidden shrink-0 md:block">{controls}</div>}
          {aside && <div data-testid="masthead-aside" className="daybook-masthead-aside">{aside}</div>}
        </div>
        {footer && <div className={open ? 'daybook-tools' : 'mt-4 flex flex-wrap items-end justify-end gap-3'}>{footer}</div>}
      </div>
    </section>
  )
}

/** The eyebrow nav for a period that isn't a single day: prev/next carets
 *  around an uppercase label, in DayNavCluster's inline grammar so Week and
 *  Month read like Today's date line. `trailing` takes a small chip ("Last
 *  month", "Back to this week"). */
export function PeriodNavEyebrow({ label, onPrev, onNext, prevLabel, nextLabel, trailing }: {
  label: ReactNode
  onPrev: () => void
  onNext: () => void
  prevLabel: string
  nextLabel: string
  trailing?: ReactNode
}) {
  return (
    <div className="relative inline-flex min-w-0 items-center gap-0.5">
      <button
        type="button"
        aria-label={prevLabel}
        onClick={onPrev}
        className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="truncate px-1.5 py-1 text-[12px] font-semibold uppercase tracking-[0.08em] text-neutral-500">
        {label}
      </span>
      <button
        type="button"
        aria-label={nextLabel}
        onClick={onNext}
        className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      {trailing}
    </div>
  )
}
